import { Component } from '@theme/component';

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const VERTEX = 'attribute vec2 position; varying vec2 uv; void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}';
const FRAGMENT = `
precision mediump float;
uniform sampler2D picture;
uniform vec2 cover;
uniform float aspect;
uniform float time;
uniform float strength;
uniform vec4 ripples[4];
varying vec2 uv;
void main(){
  vec2 offset=vec2(sin(uv.y*9.+time*.35)+cos(uv.x*7.-time*.27),cos(uv.x*8.+time*.31)+sin(uv.y*6.-time*.22))*.0018;
  for(int i=0;i<4;i++){
    vec4 r=ripples[i];
    vec2 delta=(uv-r.xy)*vec2(aspect,1.);
    float distance=length(delta);
    float age=r.z;
    float front=distance-age*.20;
    float wave=sin(front*52.)*exp(-front*front*110.)*exp(-age*.75)*r.w;
    offset+=delta/max(distance,.025)/vec2(aspect,1.)*wave*.012;
  }
  vec2 source=(uv+offset*strength-.5)*cover+.5;
  gl_FragColor=texture2D(picture,clamp(source,vec2(.001),vec2(.999)));
}`;

/** @extends {Component<{surface: HTMLElement, image?: HTMLImageElement, canvas: HTMLCanvasElement}>} */
class DLProductFilm extends Component {
  requiredRefs = ['surface', 'canvas'];

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  initialize() {
    this.cleanup();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.coarse = matchMedia('(pointer: coarse)');
    this.visible = false;
    this.elapsed = 0;
    this.lastFrame = 0;
    this.lastDrop = -4;
    this.pointerAt = 0;
    this.ripples = [];
    this.video = this.querySelector('video');
    if (this.video) {
      this.video.controls = false;
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.addEventListener('error', () => this.fallbackFromVideo(), { signal });
    }
    this.motion.addEventListener('change', () => {
      this.measure();
      this.syncPlayback();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
    window.addEventListener('scroll', () => {
      if (this.visible) this.scheduleMeasure();
    }, { signal, passive: true });
    this.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'touch') this.addPointerRipple(event);
    }, { signal, passive: true });
    this.addEventListener('pointerdown', (event) => this.addPointerRipple(event), { signal, passive: true });
    this.refs.canvas.addEventListener('webglcontextlost', () => this.disableRenderer(), { signal });
    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this);
    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) {
        this.measure();
        if (!this.video) this.prepareRenderer();
      }
      this.syncPlayback();
    }, { threshold: .01 });
    this.intersection.observe(this);
    this.measure();
  }

  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }

  cleanup() {
    this.abort?.abort();
    this.intersection?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame || 0);
    cancelAnimationFrame(this.measureFrame || 0);
    this.frame = 0;
    this.measureFrame = 0;
    if (this.textureImage) {
      this.textureImage.onload = null;
      this.textureImage.onerror = null;
    }
    if (this.video) {
      this.video.pause();
    }
    this.releaseRenderer();
    this.rendererAttempted = false;
    this.textureImage = null;
    this.playPending = false;
    delete this.dataset.renderer;
  }

  syncPlayback() {
    const active = this.visible && !document.hidden && !this.motion.matches;
    cancelAnimationFrame(this.frame || 0);
    this.frame = 0;
    this.lastFrame = 0;
    if (this.video) {
      if (active && this.video.paused && !this.playPending) {
        const video = this.video;
        const signal = this.abort.signal;
        this.playPending = true;
        video.play().then(() => {
          if (signal.aborted) { video.pause(); return; }
          this.playPending = false;
          if (!this.visible || document.hidden || this.motion.matches) video.pause();
        }).catch(() => {
          if (signal.aborted) return;
          this.playPending = false;
          if (this.visible && !document.hidden && !this.motion.matches) this.fallbackFromVideo();
        });
      } else if (!active) this.video.pause();
    } else if (active && this.gl) this.frame = requestAnimationFrame((time) => this.tick(time));
  }

  fallbackFromVideo() {
    this.video?.pause();
    if (this.video) this.video.hidden = true;
    this.video = null;
    this.prepareRenderer();
  }

  scheduleMeasure() {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = 0;
      this.measure();
    });
  }

  measure() {
    if (!this.refs.surface) return;
    const rect = this.getBoundingClientRect();
    const progress = clamp((innerHeight - rect.top) / (innerHeight + rect.height));
    const moving = !this.motion?.matches;
    this.style.setProperty('--dl-film-shift', `${moving ? (progress - .5) * (this.coarse?.matches ? 18 : 36) : 0}px`);
    this.style.setProperty('--dl-film-frame-scale', String(moving ? .94 + progress * .06 : 1));
    if (this.gl) {
      const width = this.refs.surface.clientWidth;
      const height = this.refs.surface.clientHeight;
      const ratio = Math.min(devicePixelRatio || 1, 1.5);
      const canvas = this.refs.canvas;
      const canvasWidth = Math.round(width * ratio);
      const canvasHeight = Math.round(height * ratio);
      if (canvas.width !== canvasWidth) canvas.width = canvasWidth;
      if (canvas.height !== canvasHeight) canvas.height = canvasHeight;
      const imageRatio = this.textureImage.naturalWidth / this.textureImage.naturalHeight;
      const targetRatio = width / height;
      this.cover = targetRatio > imageRatio ? [1, imageRatio / targetRatio] : [targetRatio / imageRatio, 1];
      this.aspect = targetRatio;
      this.draw();
    }
  }

  prepareRenderer() {
    if (this.rendererAttempted || !this.refs.image) return;
    this.rendererAttempted = true;
    const visibleImage = this.refs.image;
    const signal = this.abort.signal;
    const load = () => {
      if (signal.aborted || this.textureImage) return;
      const texture = new Image();
      this.textureImage = texture;
      texture.crossOrigin = 'anonymous';
      texture.onload = () => {
        if (signal.aborted) return;
        try {
          this.createRenderer(texture);
          this.measure();
          this.addRipple(.32, .58, .65);
          this.draw();
          this.dataset.renderer = 'webgl';
          this.syncPlayback();
        } catch { this.disableRenderer(); }
      };
      texture.onerror = () => { if (!signal.aborted) this.disableRenderer(); };
      texture.src = visibleImage.currentSrc || visibleImage.src;
    };
    if (visibleImage.complete && visibleImage.naturalWidth) load();
    else visibleImage.addEventListener('load', load, { once: true, signal });
  }

  createRenderer(image) {
    const gl = this.refs.canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
    if (!gl) throw new Error('WebGL unavailable');
    this.gl = gl;
    this.shaders = [];
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      this.shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Scene shader unavailable');
      return shader;
    };
    this.program = gl.createProgram();
    gl.attachShader(this.program, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(this.program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error('Scene program unavailable');
    gl.useProgram(this.program);
    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(this.program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    this.uniforms = Object.fromEntries(['picture', 'cover', 'aspect', 'time', 'strength', 'ripples[0]'].map(name => [name, gl.getUniformLocation(this.program, name)]));
    gl.uniform1i(this.uniforms.picture, 0);
    this.rippleData = new Float32Array(16);
  }

  addRipple(x, y, strength = 1) {
    this.ripples.push({ x, y, time: this.elapsed, strength });
    if (this.ripples.length > 4) this.ripples.shift();
  }

  addPointerRipple(event) {
    if (!this.gl || this.motion.matches || !this.visible || performance.now() - this.pointerAt < 100) return;
    this.pointerAt = performance.now();
    const rect = this.refs.surface.getBoundingClientRect();
    this.addRipple(clamp((event.clientX - rect.left) / rect.width), 1 - clamp((event.clientY - rect.top) / rect.height), .85);
  }

  tick(time) {
    this.frame = 0;
    if (!this.gl || !this.visible || document.hidden || this.motion.matches) return;
    if (!this.lastFrame || time - this.lastFrame >= 32) {
      this.elapsed += this.lastFrame ? Math.min(time - this.lastFrame, 80) / 1000 : 0;
      this.lastFrame = time;
      if (this.elapsed - this.lastDrop > 4.2) {
        this.lastDrop = this.elapsed;
        this.addRipple(.2 + Math.random() * .6, .22 + Math.random() * .55, .55);
      }
      this.draw();
    }
    this.frame = requestAnimationFrame((next) => this.tick(next));
  }

  draw() {
    if (!this.gl || !this.cover) return;
    const gl = this.gl;
    try {
      gl.viewport(0, 0, this.refs.canvas.width, this.refs.canvas.height);
      gl.uniform2fv(this.uniforms.cover, this.cover);
      gl.uniform1f(this.uniforms.aspect, this.aspect);
      gl.uniform1f(this.uniforms.time, this.elapsed);
      gl.uniform1f(this.uniforms.strength, this.coarse.matches ? .5 : 1);
      this.rippleData.fill(0);
      this.ripples.forEach((ripple, index) => this.rippleData.set([ripple.x, ripple.y, this.elapsed - ripple.time, ripple.strength], index * 4));
      gl.uniform4fv(this.uniforms['ripples[0]'], this.rippleData);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    } catch { this.disableRenderer(); }
  }

  disableRenderer() {
    cancelAnimationFrame(this.frame || 0);
    this.frame = 0;
    this.releaseRenderer();
    delete this.dataset.renderer;
  }

  releaseRenderer() {
    if (!this.gl) return;
    this.gl.deleteTexture(this.texture);
    this.gl.deleteBuffer(this.buffer);
    this.gl.deleteProgram(this.program);
    for (const shader of this.shaders || []) this.gl.deleteShader(shader);
    this.gl = null;
    this.program = null;
    this.texture = null;
    this.buffer = null;
    this.shaders = [];
  }
}

if (!customElements.get('dl-product-film')) customElements.define('dl-product-film', DLProductFilm);
